/**
 * site_contacts_controller.js — site-contacts manager for the quote and job
 * detail pages (Stimulus).
 *
 * Ports static/js/site_contacts.js (attachOnce('siteContactsListeners') +
 * turbo:load + turbo:before-stream-render re-init) to an element-scoped
 * controller. The controller hosts directly on the [data-site-contacts-manager]
 * element, so its lifecycle tracks that element exactly:
 *
 *   - Initial load / Turbo navigation onto the page  -> connect() binds.
 *   - Turbo Stream replace of the section (its own create/update/delete streams,
 *     or a parent re-render such as scheduling an event re-rendering
 *     client-schedule-region) swaps in a fresh manager element. Stimulus pairs
 *     disconnect() on the old element with connect() on the new one, so the
 *     handlers re-bind automatically. This is exactly what the old code's
 *     turbo:before-stream-render listener + requestAnimationFrame re-init was
 *     hand-rolling; the framework now owns it, so that document listener (and the
 *     attachOnce once-guard) are gone.
 *
 * Page-agnostic: the manager element carries data-site-id, so the same code
 * drives the section on a site, a quote or a job — all three address one set
 * of rows, the ones owned by that site. Wired to window.siteContactApi
 * (utils/site-contact-api.js), backed by window.apiClient.
 *
 * Site contacts use a single `name` field plus a free-text `notes` field
 * (site-entry instructions), unlike client contacts' first/last name split.
 *
 * data-controller="site-contacts" hosts on the [data-site-contacts-manager]
 * .section-card in templates/components/detail/site_contacts.html.
 *
 * Source (to delete): static/js/site_contacts.js
 * Loader (page_assets.py): quote_detail + job_detail extra_js_files.
 */

import { Controller } from "@hotwired/stimulus";

    function siteContactPayloadFromForm(form) {
        var formData = new FormData(form);
        return {
            name: formData.get('name') || null,
            role: formData.get('role') || null,
            email: formData.get('email') || null,
            phone: formData.get('phone') || null,
            notes: formData.get('notes') || null,
            is_primary: formData.get('is_primary') === 'on'
        };
    }

    // Thin wrapper around the site-contact API call. A Turbo Stream result swaps
    // in a fresh manager element, which Stimulus re-connects automatically (no
    // manual re-init needed). Without Turbo, fall back to a full reload.
    async function siteContactRequestStream(operation) {
        var result;
        try {
            result = await operation();
        } catch (err) {
            if (!err.message) err.message = 'Site contact update failed';
            throw err;
        }
        if (result && result.turboStream && !window.Turbo) {
            window.location.reload();
        }
        return result;
    }

    function setSiteContactFormValue(form, name, value) {
        var field = form.elements[name];
        if (!field) return;
        if (field.type === 'checkbox') {
            field.checked = value === true || value === 'true';
            return;
        }
        field.value = value || '';
    }

    function fillSiteContactEditForm(form, item) {
        setSiteContactFormValue(form, 'contact_id', item.dataset.contactId);
        setSiteContactFormValue(form, 'name', item.dataset.contactName);
        setSiteContactFormValue(form, 'role', item.dataset.contactRole);
        setSiteContactFormValue(form, 'email', item.dataset.contactEmail);
        setSiteContactFormValue(form, 'phone', item.dataset.contactPhone);
        setSiteContactFormValue(form, 'notes', item.dataset.contactNotes);
        setSiteContactFormValue(form, 'is_primary', item.dataset.contactPrimary);
    }

    function restoreSiteContactEditForm(manager, editForm) {
        if (!manager || !editForm) return;

        var editingItem = manager.querySelector('[data-contact-editing="true"]');
        if (editingItem) {
            var display = editingItem.querySelector('[data-contact-display]');
            if (display) display.classList.remove('d-none');
            delete editingItem.dataset.contactEditing;
        }

        var host = manager.querySelector('[data-contact-edit-form-host]');
        if (host && editForm.parentNode !== host) {
            host.appendChild(editForm);
        }
        editForm.classList.add('d-none');
        editForm.reset();
    }

    function showSiteContactEditForm(manager, editForm, item) {
        restoreSiteContactEditForm(manager, editForm);
        fillSiteContactEditForm(editForm, item);

        var display = item.querySelector('[data-contact-display]');
        if (display) display.classList.add('d-none');

        item.dataset.contactEditing = 'true';
        item.appendChild(editForm);
        editForm.classList.remove('d-none');
        editForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function showSiteContactAddForm(manager) {
        var form = manager.querySelector('[data-contact-form]');
        var toggle = manager.querySelector('[data-contact-add-toggle]');
        if (toggle) toggle.classList.add('d-none');
        if (form) {
            form.classList.remove('d-none');
            var firstInput = form.querySelector('input[name="name"]');
            if (firstInput) firstInput.focus();
        }
    }

    function hideSiteContactAddForm(manager) {
        var form = manager.querySelector('[data-contact-form]');
        var toggle = manager.querySelector('[data-contact-add-toggle]');
        if (form) {
            form.reset();
            form.classList.add('d-none');
        }
        if (toggle) toggle.classList.remove('d-none');
    }

    export default class extends Controller {
        connect() {
            var manager = this.element;

            var siteId = manager.dataset.siteId;
            var form = manager.querySelector('[data-contact-form]');
            var editForm = manager.querySelector('[data-contact-edit-form]');
            var editCancel = manager.querySelector('[data-contact-edit-cancel]');

            if (form && form.dataset.contactSubmitReady !== 'true') {
                form.dataset.contactSubmitReady = 'true';
                form.addEventListener('submit', async function (event) {
                    event.preventDefault();
                    try {
                        await siteContactRequestStream(function () {
                            return window.siteContactApi.create(siteId, siteContactPayloadFromForm(form));
                        });
                    } catch (error) {
                        if (typeof window.showErrorMessage === 'function') {
                            window.showErrorMessage(error.message);
                        } else {
                            alert(error.message);
                        }
                    }
                });
            }

            if (editForm && editForm.dataset.contactSubmitReady !== 'true') {
                editForm.dataset.contactSubmitReady = 'true';
                editForm.addEventListener('submit', async function (event) {
                    event.preventDefault();
                    var contactId = editForm.elements.contact_id.value;
                    try {
                        await siteContactRequestStream(function () {
                            return window.siteContactApi.update(contactId, siteContactPayloadFromForm(editForm));
                        });
                    } catch (error) {
                        if (typeof window.showErrorMessage === 'function') {
                            window.showErrorMessage(error.message);
                        } else {
                            alert(error.message);
                        }
                    }
                });
            }

            if (editCancel && editForm && editCancel.dataset.contactCancelReady !== 'true') {
                editCancel.dataset.contactCancelReady = 'true';
                editCancel.addEventListener('click', function () {
                    restoreSiteContactEditForm(manager, editForm);
                });
            }

            if (manager.dataset.contactClickReady === 'true') return;
            manager.dataset.contactClickReady = 'true';

            manager.addEventListener('click', async function (event) {
                if (event.target.closest('[data-contact-add-toggle]')) {
                    showSiteContactAddForm(manager);
                    return;
                }
                if (event.target.closest('[data-contact-add-cancel]')) {
                    hideSiteContactAddForm(manager);
                    return;
                }

                var button = event.target.closest('[data-contact-action]');
                if (!button) return;

                var item = button.closest('[data-contact-id]');
                if (!item) return;

                var contactId = item.dataset.contactId;
                var action = button.dataset.contactAction;

                try {
                    if (action === 'delete') {
                        if (!confirm('Delete this site contact?')) return;
                        await siteContactRequestStream(function () {
                            return window.siteContactApi.remove(contactId);
                        });
                        return;
                    }

                    if (action === 'edit') {
                        if (!editForm) return;
                        showSiteContactEditForm(manager, editForm, item);
                    }
                } catch (error) {
                    if (typeof window.showErrorMessage === 'function') {
                        window.showErrorMessage(error.message);
                    } else {
                        alert(error.message);
                    }
                }
            });
        }
    }
